import { z } from "zod";

import { ERROR_TYPE } from "@/utils";

import { clearMarkdownAndHTMLAndTrim } from "@/utils/clearMarkdownAndHTMLAndTrim";

import { QuestionnaireImportance } from "../enum/QuestionnaireImportance.enum";

const { REQUIRED, ACCEPT_TERMS, ANSWERS_REQUIRED } = ERROR_TYPE;

export const QuestionnaireStepOptionSchema = z.object({
	id: z.number().optional(),
	title: z.string().optional(),
	type: z.nativeEnum(QuestionnaireImportance).optional(),
});

export const QuestionnaireStepSchema = z.object({
	id: z.number().optional(),
	title: z.string().optional(),
	stepNumber: z.number().optional(),
	description: z.string().optional(),
	options: z.array(QuestionnaireStepOptionSchema).min(1, { message: ANSWERS_REQUIRED }).optional(),
	industryIds: z.array(z.number()).optional(),
	companySizeIds: z.array(z.number()).optional(),
	companyRevenueIds: z.array(z.number()).optional(),
	companyBalanceIds: z.array(z.number()).optional(),
});

export const CreateQuestionnaireStepOptionSchema = z.object({
	title: z.string().min(1, { message: REQUIRED }),
	type: z.nativeEnum(QuestionnaireImportance),
});

export const CreateQuestionnaireStepSchema = z.object({
	title: z.string().min(1, { message: REQUIRED }),
	description: z
		.string()
		.min(1, { message: REQUIRED })
		.superRefine((val, ctx) => {
			if (clearMarkdownAndHTMLAndTrim(val).trim() === "") {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: REQUIRED,
				});
			}
		}),
	options: z.array(CreateQuestionnaireStepOptionSchema).min(1, { message: ANSWERS_REQUIRED }),
	stepNumber: z.number().optional(),
	industryIds: z.array(z.number()).optional(),
	companySizeIds: z.array(z.number()).optional(),
	companyRevenueIds: z.array(z.number()).optional(),
	companyBalanceIds: z.array(z.number()).optional(),
});

export const ChangeQuestionnaireStepNumberSchema = z.object({
	id: z.number().min(1, { message: REQUIRED }),
	stepNumber: z.number().min(1, { message: REQUIRED }),
});

export const DeleteQuestionnaireStepResponseSchema = z.object({
	id: z.number(),
});

export const FinalizeQuestionnaireSchema = z
	.object({
		firstName: z.string().min(1, { message: REQUIRED }),
		lastName: z.string().min(1, { message: REQUIRED }),
		email: z.string().min(1, { message: REQUIRED }).email(),
		importance: z.nativeEnum(QuestionnaireImportance),
		companyName: z.string().min(1, { message: REQUIRED }),
		privacyStatement: z.boolean(),
		selfAssessmentAnswers: z
			.array(
				z.object({
					questionId: z.number(),
					answer: z.string().min(1, { message: REQUIRED }),
					industryIds: z.array(z.number()).optional(),
					companySizeId: z.number().nullable().optional(),
					companyRevenueId: z.number().nullable().optional(),
					companyBalanceId: z.number().nullable().optional(),
					isSubtask: z.boolean(),
					subtaskId: z.string().optional(),
					todoId: z.number().optional(),
				})
			)
			.min(1, { message: ANSWERS_REQUIRED }),
	})
	.superRefine(({ privacyStatement }, ctx) => {
		if (!privacyStatement) {
			ctx.addIssue({
				code: "custom",
				path: ["privacyStatement"],
				message: ACCEPT_TERMS,
			});
		}
	});

export const QuestionnaireArraySchema = z.array(QuestionnaireStepSchema);

export type QuestionnaireStepOption = z.infer<typeof QuestionnaireStepOptionSchema>;
export type QuestionnaireStep = z.infer<typeof QuestionnaireStepSchema>;
export type QuestionnaireStepArray = z.infer<typeof QuestionnaireArraySchema>;
export type FinalizeQuestionnaire = z.infer<typeof FinalizeQuestionnaireSchema>;
export type CreateQuestionnaireStepOption = z.infer<typeof CreateQuestionnaireStepOptionSchema>;
export type CreateQuestionnaireStep = z.infer<typeof CreateQuestionnaireStepSchema>;
export type ChangeQuestionnaireStepNumber = z.infer<typeof ChangeQuestionnaireStepNumberSchema>;
export type DeleteQuestionnaireStepResponse = z.infer<typeof DeleteQuestionnaireStepResponseSchema>;
